import React, {
  useEffect,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

import { db } from '../../services/supabase';

import { formatCurrency } from '../../utils/currency';

import '../../styles/brand-typography.css';
import AppLayout from '../../components/Layout/AppLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import InfoTooltip from '../../components/UI/InfoTooltip';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import { useFamilyStatus } from '../../contexts/FamilyStatusContext';


// ======================================================
// ChoreManagement
// ======================================================
//
// Parent Review Claims queue.
//
// Only PENDING chore claims appear here.
//
// IMPORTANT:
//
// The linked transaction is the accounting source of
// truth for the reward amount.
//
// Example:
//
// Child completes chore when worth $1.00
//     ↓
// Pending transaction = $1.00
//     ↓
// Parent later edits chore to $2.00
//     ↓
// Review screen STILL shows $1.00
//
// Approving the claim approves the original linked
// $1.00 transaction.
// ======================================================

const ChoreManagement = () => {
  const navigate = useNavigate();

  const { parent } = useAuth();

  const {
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useFamilyStatus();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;


  const [
    pendingClaims,
    setPendingClaims,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    reviewingClaimId,
    setReviewingClaimId,
  ] = useState(null);


  const [
    error,
    setError,
  ] = useState('');


  const [
    message,
    setMessage,
  ] = useState('');


  // --------------------------------------------------
  // Load pending claims
  // --------------------------------------------------

  const loadPendingClaims =
    async ({
      showLoading = false,
    } = {}) => {

      if (!parent?.family_id) {
        return;
      }


      try {
        if (showLoading) {
          setLoading(true);
        }


        setError('');


        const claims =
          await db.getChoreClaims(
            parent.family_id,
            'pending'
          );


        setPendingClaims(
          claims || []
        );
      } catch (err) {
        console.error(
          'ChoreManagement: load error:',
          err
        );


        setError(
          err?.message ||
            'Unable to load completed chores.'
        );
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    };


  // --------------------------------------------------
  // Initial load
  // --------------------------------------------------

  useEffect(() => {
    if (parent?.family_id) {
      loadPendingClaims({
        showLoading: true,
      });
    }
  }, [parent?.family_id]);


  // --------------------------------------------------
  // Review claim
  // --------------------------------------------------

  const handleClaimAction =
    async (
      claim,
      decision
    ) => {

      if (isWriteLocked) {
        return;
      }

      if (!claim?.id) {
        return;
      }

      if (claim?.child?.status === 'archived') {
        setMessage('');
        setError(
          'This completed chore is preserved but cannot be reviewed while the child is deactivated.'
        );
        return;
      }


      if (
        reviewingClaimId ===
        claim.id
      ) {
        return;
      }


      try {
        setError('');
        setMessage('');


        setReviewingClaimId(
          claim.id
        );


        // --------------------------------------------
        // Atomic database review
        // --------------------------------------------
        //
        // PostgreSQL updates BOTH:
        //
        // chore claim
        // +
        // linked transaction
        //
        // together.
        // --------------------------------------------

        const result =
          await db.reviewChoreClaim(
            claim.id,
            decision
          );


        // --------------------------------------------
        // Remove reviewed item from queue
        // --------------------------------------------

        setPendingClaims(
          (
            currentClaims
          ) =>
            currentClaims.filter(
              (
                currentClaim
              ) =>
                currentClaim.id !==
                claim.id
            )
        );


        // --------------------------------------------
        // Confirmation
        // --------------------------------------------

        if (
          decision ===
          'approved'
        ) {
          setMessage(
            `✅ Approved "${result?.chore_title || 'chore'}" ` +
              `for ${result?.child_name || 'your child'} — ` +
              `${formatCurrency(
                result?.amount_cents || 0
              )}.`
          );
        } else {
          setMessage(
            `❌ "${result?.chore_title || 'Chore'}" ` +
              `for ${result?.child_name || 'your child'} was not approved.`
          );
        }
      } catch (err) {
        console.error(
          'ChoreManagement: review error:',
          err
        );


        setError(
          err?.message ||
            'Unable to review this completed chore.'
        );


        // Another parent/session may have reviewed it.
        await loadPendingClaims();
      } finally {
        setReviewingClaimId(
          null
        );
      }
    };


  // --------------------------------------------------
  // Format completed date
  // --------------------------------------------------

  const formatCompletedDate =
    (claim) => {

      if (claim?.completed_on) {
        const date =
          new Date(
            `${claim.completed_on}T12:00:00`
          );


        return date.toLocaleDateString(
          undefined,
          {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }
        );
      }


      // Legacy fallback.
      if (claim?.claimed_at) {
        return new Date(
          claim.claimed_at
        ).toLocaleDateString(
          undefined,
          {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }
        );
      }


      return 'Unknown date';
    };


  // --------------------------------------------------
  // Chore source
  // --------------------------------------------------

  const getChoreSource =
    (claim) => {

      if (
        claim?.chore?.source
      ) {
        return claim.chore.source;
      }


      // Legacy fallback.
      if (
        claim?.chore?.created_by ===
        'child'
      ) {
        return 'child';
      }


      return 'parent';
    };


  const getSourceLabel =
    (claim) => {

      const source =
        getChoreSource(
          claim
        );


      if (
        source ===
        'child'
      ) {
        return 'Kid-created';
      }


      // Starter chores are presented as parent-created
      // chores in the parent experience.
      return 'Parent-created';
    };


  const getSourceClass =
    (claim) => {

      const source =
        getChoreSource(
          claim
        );


      if (
        source ===
        'child'
      ) {
        return (
          'bg-purple-100 dark:bg-purple-900/60 ' +
          'text-purple-700 dark:text-purple-200'
        );
      }


      return (
        'bg-blue-100 dark:bg-blue-900/60 ' +
        'text-blue-700 dark:text-blue-200'
      );
    };


  // --------------------------------------------------
  // Loading
  // --------------------------------------------------

  if (loading) {
    return (
      <AppLayout
        title="Review Completed Chores"
      className="mypiggy-brand"
      >
        <div className="text-center py-8">
          Loading...
        </div>
      </AppLayout>
    );
  }


  // --------------------------------------------------
  // Page
  // --------------------------------------------------

  return (
    <AppLayout
      title="Review Completed Chores"
      className="mypiggy-brand"
      showBack
      onBack={() =>
        navigate('/parent')
      }
    >
      <div className="space-y-3">

        {/* ========================================
            Heading
           ======================================== */}

        <div className="flex flex-wrap items-center justify-between gap-3">

          <div>
            <div className="flex items-center gap-2">
              <MyPiggyIcon name="chores-submitted-for-approval" size="sm" />
              <h2 className="text-xl font-extrabold text-gray-900 dark:text-slate-100">
                Review Completed Chores
              </h2>

              <InfoTooltip compact
                text="These are completed chores from children who require parent approval. Approving a chore approves its linked reward transaction; rejecting it does not add the reward to Available Money."
                ariaLabel="About chore approvals"
              />
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
              Review chores your kids marked as completed.
            </p>
          </div>


          <span
            className={
              pendingClaims.length > 0
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-300 dark:text-amber-950 px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap'
                : 'bg-green-100 text-green-800 dark:bg-green-300 dark:text-green-950 px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap'
            }
          >
            {pendingClaims.length}{' '}
            pending
          </span>

        </div>


        {/* ========================================
            Info
           ======================================== */}

        <Card className="p-3 bg-blue-50 dark:bg-blue-950/35 border-blue-200 dark:border-blue-800">

          <div className="flex items-start gap-3 text-sm text-blue-800 dark:text-blue-200">
            <MyPiggyIcon name="learning-tip" size="sm" />
            <p>Only children who have{' '}
            <strong>
              Require Parent Approval for Chores
            </strong>{' '}
            enabled will have completed
            chores appear here.</p>
          </div>

        </Card>


        {/* ========================================
            Success
           ======================================== */}

        {message ? (
          <Card className="p-4 bg-green-50 dark:bg-green-950/35 border-green-200 dark:border-green-800">

            <p role="status" className="text-sm text-green-800 dark:text-green-200 font-bold">
              {message}
            </p>

          </Card>
        ) : null}


        {/* ========================================
            Error
           ======================================== */}

        {error ? (
          <Card className="p-4 bg-red-50 dark:bg-red-950/35 border-red-200 dark:border-red-800">

            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              {error}
            </p>

          </Card>
        ) : null}


        {/* ========================================
            Empty
           ======================================== */}

        {pendingClaims.length ===
        0 ? (

          <Card className="p-5 text-center">

            <MyPiggyIcon name="celebration-achievement" size="md" className="mb-2" />


            <p className="text-lg font-semibold text-gray-800 dark:text-slate-200">
              You're all caught up!
            </p>


            <p className="text-sm text-gray-500 dark:text-slate-300 mt-2">
              No completed chores are
              waiting for approval.
            </p>

          </Card>

        ) : (

          // ======================================
          // Pending claims
          // ======================================

          <div className="space-y-3">

            {pendingClaims.map(
              (claim) => {

                const isReviewing =
                  reviewingClaimId ===
                  claim.id;


                const choreTitle =
                  claim?.chore?.title ||
                  'Untitled chore';


                const childName =
                  claim?.child?.nickname ||
                  'Kid';

                const childDeactivated =
                  claim?.child?.status === 'archived';


                // ==================================
                // IMPORTANT:
                // Transaction amount is the source
                // of truth.
                // ==================================

                const rewardAmount =
                  claim?.transaction
                    ?.amount_cents ??
                  claim?.chore
                    ?.value_cents ??
                  0;


                const hasTransaction =
                  Boolean(
                    claim?.transaction_id &&
                    claim?.transaction?.id
                  );


                const transactionPending =
                  claim?.transaction
                    ?.approval_status ===
                  'pending';


                return (
                  <Card
                    key={
                      claim.id
                    }
                    className="p-3 sm:p-4"
                  >

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">


                      {/* ============================
                          Details
                         ============================ */}

                      <div className="min-w-0 flex-1">


                        <div className="flex flex-wrap items-center gap-2 mb-1">


                          <h3 className="font-extrabold text-base text-gray-900 dark:text-slate-100">
                            {choreTitle}
                          </h3>


                          <span className="bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-200 px-2 py-1 rounded text-sm font-semibold">
                            {formatCurrency(
                              rewardAmount
                            )}
                          </span>


                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${getSourceClass(
                              claim
                            )}`}
                          >
                            {getSourceLabel(
                              claim
                            )}
                          </span>

                        </div>


                        <p className="text-sm text-gray-700 dark:text-slate-300 mb-1">
                          Completed by:{' '}

                          <span className="font-semibold">
                            {childName}
                          </span>

                          {childDeactivated && (
                            <span className="ml-2 rounded-full bg-amber-100 dark:bg-amber-950/60 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
                              Deactivated
                            </span>
                          )}
                        </p>


                        <p className="text-sm text-gray-500 dark:text-slate-300">
                          Completed:{' '}

                          {formatCompletedDate(
                            claim
                          )}
                        </p>


                        {/* --------------------------
                            Transaction status
                           -------------------------- */}

                        {hasTransaction &&
                        transactionPending ? (

                          <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                            Ready for review
                          </p>

                        ) : !hasTransaction ? (

                          <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                            ⚠ This older completed chore has no
                            linked reward transaction.
                          </p>

                        ) : (

                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                            ⚠ The linked transaction is
                            no longer pending.
                          </p>

                        )}

                        {childDeactivated && (
                          <p className="mt-3 rounded-lg border border-amber-200 dark:border-amber-900/70 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                            This completed chore is preserved and can be reviewed after the child is reactivated.
                          </p>
                        )}

                      </div>


                      {/* ============================
                          Actions
                         ============================ */}

                      <div className="flex shrink-0 gap-2 md:ml-3">


                        <Button
                          className="font-extrabold"
                          variant="danger"
                          size="sm"
                          disabled={
                            isWriteLocked ||
                            childDeactivated ||
                            isReviewing ||
                            !hasTransaction ||
                            !transactionPending
                          }
                          onClick={() =>
                            handleClaimAction(
                              claim,
                              'rejected'
                            )
                          }
                        >
                          {isReviewing
                            ? 'Saving...'
                            : 'Reject'}
                        </Button>


                        <Button
                          className="font-extrabold"
                          variant="success"
                          size="sm"
                          disabled={
                            isWriteLocked ||
                            childDeactivated ||
                            isReviewing ||
                            !hasTransaction ||
                            !transactionPending
                          }
                          onClick={() =>
                            handleClaimAction(
                              claim,
                              'approved'
                            )
                          }
                        >
                          {isReviewing
                            ? 'Saving...'
                            : 'Approve'}
                        </Button>

                      </div>

                    </div>

                  </Card>
                );
              }
            )}

          </div>
        )}

      </div>
    </AppLayout>
  );
};


export default ChoreManagement;

